import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/content.functions";
import { NAV } from "@/lib/site";

type Item = {
  title: string;
  subtitle?: string;
  kind: "Página" | "Artigo";
  to: string;
  params?: Record<string, string>;
  haystack: string;
};

const STATIC_PAGES: Item[] = NAV.map((n) => ({
  title: n.label,
  kind: "Página" as const,
  to: n.to,
  haystack: n.label.toLowerCase(),
}));

export function SiteSearch({ compact = false }: { compact?: boolean }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: posts = [] } = useQuery({
    queryKey: ["search-posts"],
    queryFn: () => listPublishedPosts(),
    staleTime: 5 * 60_000,
  });

  const items: Item[] = useMemo(() => {
    const a: Item[] = [...STATIC_PAGES];
    for (const p of posts) {
      a.push({
        title: p.title,
        subtitle: p.description || p.excerpt || "",
        kind: "Artigo",
        to: "/blog/$slug",
        params: { slug: p.slug },
        haystack: `${p.title} ${p.excerpt} ${p.category}`.toLowerCase(),
      });
    }
    return a;
  }, [posts]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [] as Item[];
    return items.filter((i) => i.haystack.includes(term)).slice(0, 8);
  }, [q, items]);

  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function go(item: Item) {
    setOpen(false);
    setQ("");
    navigate({ to: item.to as any, params: item.params as any });
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (results[active]) {
        e.preventDefault();
        go(results[active]);
      } else if (q.trim()) {
        e.preventDefault();
        setOpen(false);
        navigate({ to: "/blog", search: { q: q.trim() } as any });
        setQ("");
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${compact ? "w-full" : "w-full max-w-xs"}`}>
      <div className="group relative flex items-stretch">
        <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Buscar notícias, editorias, matérias..."
          aria-label="Buscar no site"
          className="w-full rounded-none border-2 border-primary bg-background py-2 pl-10 pr-24 text-sm font-medium text-foreground placeholder:text-muted-foreground placeholder:font-normal transition focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <button
          type="button"
          onClick={() => {
            if (q.trim()) {
              setOpen(false);
              navigate({ to: "/blog", search: { q: q.trim() } as any });
              setQ("");
            }
          }}
          className="absolute right-0 top-0 bottom-0 bg-primary px-3 text-[11px] font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90 transition"
          aria-label="Buscar"
        >
          Buscar
        </button>
      </div>
      {open && q.trim().length > 0 && (
        <div className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-none border-2 border-primary bg-background shadow-[0_20px_60px_-20px_oklch(0_0_0_/_0.35)]">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              Nenhum resultado para "{q}". Tente outra palavra-chave.
            </div>
          ) : (
            <ul role="listbox" className="max-h-[60vh] overflow-y-auto py-1">
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.title}-${i}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    className={`flex w-full flex-col gap-0.5 px-4 py-2.5 text-left transition ${
                      i === active ? "bg-secondary" : "hover:bg-secondary"
                    }`}
                  >
                    <span className="block text-sm font-medium text-foreground">{r.title}</span>
                    {r.subtitle && (
                      <span className="block line-clamp-2 text-xs text-muted-foreground">{r.subtitle}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
            <kbd className="rounded bg-secondary px-1.5 py-0.5">↑↓</kbd> navegar
            <span className="mx-2">·</span>
            <kbd className="rounded bg-secondary px-1.5 py-0.5">Enter</kbd> abrir
            <span className="mx-2">·</span>
            <Link to="/blog" className="text-primary hover:underline" onClick={() => setOpen(false)}>
              ver todo o blog
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
